// Globally Scoped Types
// ==== Enums ====
export enum EPlatforms {
  StoneXClient = "StoneX Client",
  StoneXPro = "Vulcan Pro",
  StoneXOne = "StoneX One",
}

export enum EApplicationThemes {
  LightMode = "lightMode",
  DarkMode = "darkMode",
}

export enum ERoutePaths {
  // ==== Global ====
  BASE = "/l",
  CONTACT = "/l/contact",
  SIGNUP = "/l/signup",
  // ==== Migration ====
  REGISTER = "/l/register",
  MIGRATION_WEALTH_ACCESS = "/l/migration/wealthaccess/:id/queue",
  INVESTOR_RELATIONS = "/l/investor-relations",
  // ==== SelfEnroll ====
  SELF_ENROLL = "/l/signup/selfenroll",
  // ==== Onboarding ====
  ONBOARDING = "/l/signup/onboarding",
  MARGIN = "/l/margin",
  OPTIONS = "/l/options",
}

export enum ELocalStorageKeys {
  GTM_KEY = "gtmKey",
  USER_EMAIL = "userEmail",
}

export enum EHttpMethods {
  GET = "GET",
  POST = "POST",
  PUT = "PUT",
  DELETE = "DELETE",
  PATCH = "PATCH",
}

/**
 * Feature flag names, as published in Azure App Configuration.
 *
 * Values must be lower case, prefixed `ff-`, and free of whitespace.
 *
 * `AuthenticationStore.getFeatureFlag` matches names case-insensitively and
 * strips all whitespace, so neither affects resolution — keeping the values
 * uniform simply removes the temptation to hand-match them against Azure App
 * Configuration, which is where flag names are typed by hand and where a
 * mismatch used to make a feature read as "off" with no error. Enforced by
 * src/tests/utilities/featureFlagUtilities.test.ts.
 */
export enum FEATURE_FLAGS {
  MAXIT = "ff-maxit",
  FOREIGN_ACCOUNT = "ff-foreignaccount",
  CHAT_MIGRATION = "ff-62557",
  ONBOARDING_REDIRECT = "ff-onboardingredirect",
}

/**
 * Where the onboarding, margin and options flows send users while
 * `FEATURE_FLAGS.ONBOARDING_REDIRECT` is on.
 *
 * Self-service account opening moved to StoneX One; the Lander flows stay in the
 * bundle behind the flag so they can be restored by toggling it off rather than
 * by a deployment.
 */
export const ONBOARDING_REDIRECT_URL = "https://stonexone.com/";

// ==== Interfaces ====
export interface IConfig {
  authRedirect: string;
  clientUri: string;
  defaultPrivateLabelCode: string;
  /**
   * Feature flags exactly as `/l/config` returns them.
   *
   * The keys are authored by hand in Azure App Configuration, so this is an
   * arbitrary string map: a flag the client knows about may be absent, and flags
   * the client knows nothing about may be present. Typing it as a complete
   * `Record<FEATURE_FLAGS, boolean>` would assert that every flag is always
   * supplied, which is never true — locally only a handful are defined — and
   * would hide missing keys from the compiler.
   *
   * Do not index this map directly. Read flags through
   * `AuthenticationStore.getFeatureFlag`, which resolves names
   * case-insensitively.
   */
  featureFlags: Partial<Record<string, boolean>>;
  instrumentationKey: string;
  oneStonexUri: string;
  proUri: string;
  torchUri: string;
}

export interface IPrivateLabelSetting {
  blsUserId: string;
  id: string;
  privateLabelCode: string;
  repCode: string;
  returnLink: string;
  subNumber: string;
}

export interface IFormData {
  [field: string]: any;
}

export interface IPhoneNumber {
  countryCode: string;
  phone: string;
}

export interface IRequestPreview {
  id: string;
  metadataModel: {
    migrationValue: string;
    pin: null;
    timestamp: Date;
    userId: string;
    userInfo: {
      accounts: {
        close: IRequestPreviewAccounts[];
        open: IRequestPreviewAccounts[];
      };
      email: string;
      firstName: string;
      lastName: string;
      phoneNumber: IPhoneNumber;
      userId: string;
    };
  };
  metadataName: string;
}

interface IRequestPreviewAccounts {
  acctNo: string;
  disabled: boolean;
  entitlement: "PRIMARY" | "SECONDARY" | null;
  isSubscriptionAccount: boolean;
  name: string | null;
}

export interface ICountryCode {
  code: string;
  name: string;
}

export interface IPhoneCountryCode {
  countryCode: string;
  countryName: string;
}
